"use client";
import React from "react";
import { MapPin, Navigation, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useOutlet } from "@/context/OutletContext";

const LocationPrompt = () => {
    const { geoStatus, requestLocation } = useOutlet();
    const [isVisible, setIsVisible] = React.useState(false);

    // Show prompt if location is not granted
    React.useEffect(() => {
        // Basic check for mobile-like width on mount
        const checkVisibility = () => {
            const isMobile = window.innerWidth < 768;
            if (isMobile && (geoStatus === "idle" || geoStatus === "denied")) {
                setIsVisible(true);
            } else {
                setIsVisible(false);
            }
        };

        // Initial delay for better UX
        const timer = setTimeout(checkVisibility, 2000);

        window.addEventListener("resize", checkVisibility);
        return () => {
            clearTimeout(timer);
            window.removeEventListener("resize", checkVisibility);
        };
    }, [geoStatus]);

    if (!isVisible) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ y: -100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -100, opacity: 0 }}
                className="fixed top-20 left-4 right-4 z-[45] md:hidden"
            >
                <div className="bg-card/95 backdrop-blur-md border border-primary/20 shadow-xl shadow-primary/10 rounded-2xl p-4 flex items-center gap-4 relative overflow-hidden">
                    {/* Subtle background decoration */}
                    <div className="absolute -right-4 -top-4 w-20 h-20 bg-primary/5 rounded-full blur-2xl" />

                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-6 h-6 text-primary" />
                    </div>

                    <div className="flex-1">
                        <h4 className="text-sm font-bold text-foreground leading-tight">Enable Location Access</h4>
                        <p className="text-[11px] text-muted-foreground mt-0.5 leading-tight">
                            To find the nearest outlet and see accurate pricing.
                        </p>
                    </div>

                    <button
                        onClick={() => {
                            requestLocation();
                            setIsVisible(false);
                        }}
                        className="px-4 py-2 bg-primary text-primary-foreground text-xs font-bold rounded-xl shadow-lg shadow-primary/20 active:scale-95 transition-all flex items-center gap-1.5"
                    >
                        <Navigation className="w-3 h-3" />
                        Enable
                    </button>

                    <button
                        onClick={() => setIsVisible(false)}
                        title="Dismiss"
                        aria-label="Dismiss location request"
                        className="absolute top-2 right-2 p-1 text-muted-foreground/40 hover:text-foreground rounded-full transition-colors"
                    >
                        <X className="w-3 h-3" />
                    </button>
                </div>
            </motion.div>
        </AnimatePresence>
    );
};

export default LocationPrompt;
